'use client';

import React, { useEffect, useRef, useState } from 'react';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PortableTextBlock } from 'sanity';
import { cn } from '@/lib/utils';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from '@/helpers/Image';
import { GROQImage } from '@/sanity/lib/definitions';

import bg from '@/public/challenge/bgCard.png';
import checkboxDark from '@/public/comp-affiliate-old-design/checkboxdark.svg';
import checkboxLight from '@/public/comp-affiliate-old-design/checkboxlight.svg';
import iqIcon from '@/public/comp-affiliate-old-design/iqIcon.svg';

gsap.registerPlugin(ScrollTrigger);

type ProgressItem = {
  icon?: GROQImage;
  placement?: string;
  text: PortableTextBlock[];
  backgroundColor: string;
  list: string[];
};

export default function ProgressItems({ items }: { items?: ProgressItem[] }) {
  const [activeItems, setActiveItems] = useState<Set<number>>(new Set());
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!items) return;

    dotRefs.current = dotRefs.current.slice(0, items.length);

    items.forEach((_, index) => {
      const dotElement = dotRefs.current[index];
      if (!dotElement) return;

      gsap.set(dotElement, { scale: 0, opacity: 0 });

      ScrollTrigger.create({
        trigger: dotElement,
        start: 'center center',
        end: 'center center',
        onEnter: () => {
          gsap.to(dotElement, { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.7)' });
          setActiveItems((prev) => new Set(prev).add(index));
        },
        onLeaveBack: () => {
          gsap.to(dotElement, { scale: 0, opacity: 0, duration: 0.3, ease: 'power2.out' });
          setActiveItems((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
          });
        },
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, [items]);

  const currentActiveIndex = activeItems.size > 0 ? Math.max(...Array.from(activeItems)) : -1;

  if (!items || items.length === 0) return null;

  return (
    <div className="relative">
      <div className="hidden sm:block absolute h-full left-0">
        <div className="rounded-full w-full h-full p-[1.5rem]">
          <div className="w-[0.25rem] h-full">
            <div className="w-full h-full relative overflow-clip">
              <div
                id="progressColor"
                className="sticky top-[50%] after:h-[100vh] after:w-full after:bg-[#6DA991] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="pl-0 sm:pl-[8rem] lg:pl-[12rem] flex flex-col gap-[4rem]">
        {items.map((item, index) => (
          <div key={index} className="relative">
            <div className="hidden sm:block absolute -left-[8.25rem] lg:-left-[12.25rem] top-[1rem]">
              <div className="relative">
                <div className="w-[3.875rem] h-[3.875rem] p-[1px]">
                  <div className="rounded-full w-full h-full bg-[#16414B]" />
                </div>
                <div
                  ref={(el) => { dotRefs.current[index] = el; }}
                  className="absolute inset-0 z-10 w-[3.875rem] h-[3.875rem] rounded-full bg-[#6DA991] p-[2px]"
                >
                  <div className="rounded-full w-full h-full bg-[#103037] p-[0.88rem]">
                    <div className="rounded-full w-full h-full bg-[#6DA991]" />
                  </div>
                </div>
              </div>
            </div>

            <div
              className={cn(
                'rounded-[2rem] lg:rounded-[2.25rem] py-[2.5rem] sm:py-[3rem] px-[3rem] sm:px-[4rem] overflow-clip transition-opacity duration-500 relative',
                index <= currentActiveIndex ? 'opacity-100' : 'opacity-50',
              )}
              style={{ backgroundColor: item.backgroundColor }}
            >
              {index < 3 && (
                <div
                  className="absolute top-0 left-0 w-full h-full pointer-events-none"
                  style={{ backgroundColor: item.backgroundColor }}
                >
                  <Image
                    className="absolute left-1/2 -translate-x-1/2 top-0 !w-full object-cover mix-blend-luminosity"
                    style={{
                      maskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
                    }}
                    src={bg}
                    quality={100}
                    alt=""
                  />
                </div>
              )}

              <div className="flex flex-col items-start gap-[4rem] justify-between h-full relative z-[5]">
                {item.icon ? (
                  <Image src={item.icon} alt="" className="!w-[7.375rem]" />
                ) : index < 3 ? (
                  <Image src={iqIcon} alt="" className="!w-[7.375rem]" />
                ) : null}
                {!item.icon && index >= 3 && item.placement && (
                  <span className="text-[4rem] font-medium font-geist bg-gradient-to-b from-[#33616B] to-[#396A75] bg-clip-text text-transparent">
                    {item.placement}
                  </span>
                )}

                {item?.text && (
                  <div
                    className={cn(
                      'richtext richtext-start flex flex-col gap-[1.5rem] prose-headings:font-geist prose-headings:text-[3rem] prose-headings:font-medium prose-headings:tracking-[-0.09rem] [text-wrap-style:pretty]',
                      index < 3 ? 'prose-headings:text-[#103037]' : 'prose-headings:text-white',
                    )}
                  >
                    <PortableText value={item.text} components={richTextComponent} />
                  </div>
                )}
              </div>

              <div className="flex flex-col items-start gap-[1rem] relative z-[5] mt-[2.25rem]">
                {item?.list?.map((bull, indexb) => (
                  <div
                    key={'bull' + index + indexb}
                    className="flex items-center gap-[1.5rem]"
                  >
                    {index < 3 ? (
                      <Image src={checkboxDark} alt="" className="!w-[2.375rem] shrink-0" />
                    ) : (
                      <Image src={checkboxLight} alt="" className="!w-[2.375rem] shrink-0" />
                    )}
                    <span
                      className={cn(
                        'font-geist text-[2rem] font-medium tracking-[-0.09rem] [text-wrap-style:pretty]',
                        index < 3 ? 'text-[#103037]' : 'text-white/80',
                      )}
                    >
                      {bull}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
